import { PerspectiveCamera } from "@react-three/drei"
import { useFrame } from "@react-three/fiber"
import * as THREE from "three"
import { useActsStore } from "../../../store/useActsStore"
import { moveAxisRange } from "../../../utils/moveObjects"
import { useIsMobile } from "../../../utils/useIsMobile"
import { useRef } from "react"
import { useDeviceTilt } from "../../../utils/useDeviceTilt"


export default function CameraOne({ innerRef, pointerRef }) {
  const isMobile = useIsMobile()
  const camPos = isMobile ? [0.7, 0.35, 0.08] : [0.55, 0.27, 0.17]
  const camRot = [-1.44, 0.77, 1.43]

  const cursorOffsetRef = useRef({ x: 0, z: 0 })
  const rotOffsetRef = useRef({ x: 0, y: 0 })

  const getAngles = useDeviceTilt()

  useFrame((state, delta) => {
    const cam = innerRef.current
    if (!cam) return

    const store = useActsStore.getState()
    const id = store.getCurrentActId()
    if (id !== 0) return

    const phase = store.actsProgress?.[id]?.phase
    const holdProgress = store.actsProgress?.[id]?.holdProgress ?? 0

    // anti-cumul offsets
    cam.position.x -= cursorOffsetRef.current.x
    cam.position.z -= cursorOffsetRef.current.z
    cam.rotation.x -= rotOffsetRef.current.x
    cam.rotation.y -= rotOffsetRef.current.y

    // tes moves existants
    moveAxisRange(innerRef, "x", camPos[0], -0.5, holdProgress, 0.2, 1, 10, delta)
    moveAxisRange(innerRef, "y", camPos[1], -0.05, holdProgress, 0.2, 0.5, 10, delta)

    const smooth = 1 - Math.exp(-14 * delta)

    // -------------------------
    // 1) OFFSET POSITION DESKTOP (pointerRef)
    // -------------------------
    let tx = 0
    let tz = 0

    if (phase === "hold" && !isMobile && pointerRef?.current) {
      const ampX = 0.03
      const ampZ = 0.03

      const px = THREE.MathUtils.clamp(pointerRef.current.x, -1, 1)
      const py = THREE.MathUtils.clamp(pointerRef.current.y, -1, 1)

      // petit parallax
      tx = -py * ampX
      tz = -px * ampZ
    }

    cursorOffsetRef.current.x = THREE.MathUtils.lerp(cursorOffsetRef.current.x, tx, smooth)
    cursorOffsetRef.current.z = THREE.MathUtils.lerp(cursorOffsetRef.current.z, tz, smooth)

    cam.position.x += cursorOffsetRef.current.x
    cam.position.z += cursorOffsetRef.current.z

    // -------------------------
    // 2) OFFSET ROTATION MOBILE (gyro)
    // -------------------------
    let rx = 0
    let ry = 0

    if (phase === "hold" && isMobile) {
      const a = getAngles()

      const pitchRaw = a.pitch
      const rollRaw = a.roll

      const pitchNeutral = THREE.MathUtils.degToRad(50)
      const pitchDelta = pitchRaw - pitchNeutral

      const pitch = THREE.MathUtils.clamp(pitchDelta, -0.6, 0.6)
      const roll = THREE.MathUtils.clamp(rollRaw, -0.6, 0.6)

      const ampX = 0.25
      const ampY = 0.25

      rx = roll * ampX
      ry = pitch * ampY
    }

    rotOffsetRef.current.x = THREE.MathUtils.lerp(rotOffsetRef.current.x, rx, smooth)
    rotOffsetRef.current.y = THREE.MathUtils.lerp(rotOffsetRef.current.y, ry, smooth)

    cam.rotation.x += rotOffsetRef.current.x
    cam.rotation.y += rotOffsetRef.current.y
  })

  return (
    <PerspectiveCamera
      ref={innerRef}
      position={camPos}
      rotation={camRot}
      fov={64}
      near={0.1}
    />
  )
}
